// B4 (PLAN) — « Collectivités : comment acheter » : le différenciateur immédiat.
// Répond aux 3 questions d'un DGS : devis ? Chorus Pro ? mandat administratif ?
// Le wording « activation à réception du bon de commande » suit la reco de Marc
// (note réunion, décision A) — ajustable en 1 ligne si l'arbitrage diffère.
import { Link } from 'react-router-dom'
import AppLayout from './AppLayout'
// Charte de l'observatoire (audit des anciennes chartes, 11/10) : jetons `.obs2-jetons`,
// cartes à bordure seule, boutons marine. Les pastilles d'icônes décoratives sont retirées.
import './obs/obs.css'
import { usePageMeta } from './hooks/usePageMeta'
import { NOM_PRODUIT } from './constants'
import { useStructure } from './structure'

const ETAPES: { titre: string; texte: string }[] = [
  {
    titre: '1. Recevez votre devis',
    texte: "Depuis la page de votre commune sur l'observatoire, demandez votre devis : il est " +
      "généré immédiatement, envoyé par email en PDF, valable 90 jours. Le tarif est indexé " +
      "sur la population de votre commune (de 600 à 12 000 € HT/an, tout inclus). " +
      "Au-delà de 500 000 habitants, l'abonnement fait l'objet d'un devis dédié — contactez-nous directement.",
  },
  {
    titre: '2. Émettez votre bon de commande',
    texte: "Votre collectivité émet son bon de commande en rappelant la référence du devis. " +
      "Si la valeur totale du contrat, reconductions comprises, reste sous le seuil de dispense, aucune mise en concurrence n’est requise.",
  },
  {
    titre: '3. Votre accès est activé',
    texte: "À réception du bon de commande, nous activons l'abonnement — sans attendre le " +
      "paiement. Vos équipes accèdent immédiatement aux {nb} thématiques, benchmarks et analyses.",
  },
  {
    titre: '4. Facture via Chorus Pro',
    texte: "La facture est déposée sur Chorus Pro, la plateforme obligatoire de facturation " +
      "du secteur public. Rien à mettre en place de votre côté : votre trésorerie la reçoit " +
      "par le circuit habituel.",
  },
  {
    titre: '5. Règlement par mandat administratif',
    texte: "Vous réglez par mandat administratif (virement du Trésor public), dans le délai " +
      "global de paiement de 30 jours. Aucune carte bancaire n'est nécessaire.",
  },
]

export default function CollectivitesPage() {
  // A20 : `{nb}` dans un texte d'étape = le nombre de thématiques servi par le registre
  // (`useStructure`, repli constants.ts) — jamais un nombre écrit à la main.
  const { nb_themes } = useStructure()
  const avecNombre = (x: string) => x.split('{nb}').join(String(nb_themes))
  usePageMeta({
    title: `Collectivités : comment s'abonner — ${NOM_PRODUIT}`,
    description: "Devis immédiat, bon de commande, facture Chorus Pro, mandat administratif : " +
      `le circuit d'achat ${NOM_PRODUIT} est conçu pour les communes. Sans carte bancaire.`,
  })

  return (
    <AppLayout>
      <div className="obs2-jetons max-w-3xl mx-auto px-4 py-12 text-(--c-text)">
        <h1 className="obs2-h1">
          Collectivités : comment s'abonner
        </h1>
        <p className="mb-10 leading-relaxed">
          {NOM_PRODUIT} est conçu pour l'achat public : <strong>devis</strong>, <strong>bon de
          commande</strong>, <strong>facturation Chorus Pro</strong> et <strong>mandat
          administratif</strong>. Pas de carte bancaire, pas de compte à ouvrir chez un
          prestataire de paiement — votre circuit habituel.
        </p>

        <div className="space-y-3 mb-10">
          {ETAPES.map(({ titre, texte }) => (
            <section key={titre} className="obs2-card">
              <h2 className="obs2-card-title">{titre}</h2>
              <p className="text-sm leading-relaxed">
                {avecNombre(texte)}
              </p>
            </section>
          ))}
        </div>

        <section className="obs2-card mb-4">
          <h2 className="obs2-card-title">Et la carte bancaire ?</h2>
          <p className="text-sm leading-relaxed">
            La carte bancaire est proposée uniquement par un lien envoyé après validation du devis par notre équipe.
            Le virement bancaire sur devis et le mandat administratif sont acceptés.
          </p>
          <p className="text-sm text-(--c-muted) leading-relaxed mt-3">
            Le devis reste disponible dans tous les cas : rien ne vous oblige à payer en
            ligne, et le mandat administratif ne demande aucune carte.
          </p>
        </section>

        <section className="obs2-card mb-6">
          <h2 className="obs2-card-title">Sans compte sur le site ?</h2>
          <p className="text-sm leading-relaxed">
            Le devis se demande en ligne : un compte gratuit (30 secondes) suffit,
            <strong> votre devis arrive</strong> immédiatement par email. Pour toute autre
            question, la <Link to="/contact" className="text-(--c-brand) underline underline-offset-2">page contact</Link> reste
            ouverte — réponse sous deux jours ouvrés.
          </p>
        </section>

        <div className="obs2-card text-center">
          <h2 className="obs2-card-title">Prêt à essayer ?</h2>
          <p className="text-sm mb-4">
            Consultez l'observatoire de votre commune et demandez votre devis en 1 minute.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/#recherche" className="obs2-btn obs2-btn-primary">
              Voir ma commune
            </Link>
            <Link to="/contact" className="obs2-btn obs2-btn-ghost">
              Demander une démo
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
